import { useEffect, useState } from "react";
import { Download, Wallet } from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

const fmtQar = (v) => `QAR ${Number(v || 0).toLocaleString()}`;

export default function MyPayslipsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/payroll/my/payslips").then((r) => setData(r.data)).catch(() => setData({ payslips: [] }));
  }, []);

  if (!data) return <Skeleton className="h-80 w-full rounded-none" />;

  return (
    <div className="space-y-5 fade-in max-w-4xl" data-testid="my-payslips-page">
      <div className="bg-white border border-border p-5 flex items-center gap-4">
        <div className="h-10 w-10 bg-[var(--brand-primary)] text-white flex items-center justify-center">
          <Wallet className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-[15px] font-bold text-[var(--brand-primary)]">My Payslips</h2>
          <p className="text-[12px] text-muted-foreground">
            Available once a payroll run is approved. QID is masked on every payslip per PII policy.
          </p>
        </div>
      </div>

      <div className="bg-white border border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Period", "Status", "Gross", "Incentive", "Pension", "Net Pay", ""].map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.payslips.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[13px] text-muted-foreground">
                No approved payslips yet.
              </td></tr>
            )}
            {data.payslips.map((p) => (
              <tr key={p.run_id} data-testid={`payslip-row-${p.period}`}>
                <td className="px-4 py-3 font-mono font-semibold text-[var(--brand-primary)]">{p.period}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono px-2 py-1 uppercase bg-teal-600 text-white">{p.status}</span>
                </td>
                <td className="px-4 py-3 font-mono">{fmtQar(p.gross)}</td>
                <td className="px-4 py-3 font-mono text-teal-700">{fmtQar(p.incentive)}</td>
                <td className="px-4 py-3 font-mono">{p.pension ? fmtQar(p.pension) : "—"}</td>
                <td className="px-4 py-3 font-mono font-bold">{fmtQar(p.net)}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`payslip-download-${p.period}`}
                    onClick={() => downloadFile(`/payroll/runs/${p.run_id}/payslip/${user.employee_id}`, `payslip-${p.period}.pdf`)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-medium text-teal-700 hover:text-teal-900 border border-teal-600/40 px-2.5 py-1.5">
                    <Download className="h-3.5 w-3.5" /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
